// ** React Imports
import { ChangeEvent, ElementType, useEffect, useMemo, useState } from 'react';

// ** MUI Imports
import Drawer from '@mui/material/Drawer';
import Select from 'src/features/common/CustomSelect';
import Button, { ButtonProps } from '@mui/material/Button';
import { styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import Box, { BoxProps } from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import AddIcon from '@mui/icons-material/Add';
import { v4 as uuid } from 'uuid';
// ** Third Party Imports
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';

// ** Icons Imports
import Close from 'mdi-material-ui/Close';

// ** Store Imports
import { client } from 'src/utils/axios-client';
import { Account, AccountKeyword } from '../../stores/AccountStore';
import { ApiResponseType } from '../../types';
import ApplicationStore from '../../stores/ApplicationStore';
import { loadAccountImageEndpoint, saveAccountEndpoint } from '../../configs/endpoints';
import RenderIf, { ClaimType, ClaimValue } from '../common/RenderIf';
import SimpleDialog from '../common/SimpleDialog';
import { Chip, Grid } from '@mui/material';
import { isEmpty } from 'lodash';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SectionAppBar from '../../v2/components/Section/SectionAppBar';
import FormWithLoader from '../../v2/components/FormWithLoader/FormWithLoader';
import StandardButton from '../../v2/components/Button/StandardButton';
import SaveButton from '../../v2/components/Button/SaveButton';
import useDuplicateAccountList from '../../v2/features/Accounts/DuplicateAccountList/hooks/DuplicateAccountList.hooks';
import { DuplicateAccount } from '../../v2/features/Accounts/DuplicateAccountList/types/types';
import DuplicateAccountList from '../../v2/features/Accounts/DuplicateAccountList/DuplicateAccountList';

interface Props {
    open: boolean;
    onClose: () => void;
    onSaved: (data: Account) => void;
    account: Account;
}

const Header = styled(Box)<BoxProps>(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3, 4),
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
}));

const ImgStyled = styled('img')(({ theme }) => ({
    width: 120,
    height: 120,
    marginRight: theme.spacing(6.25),
    borderRadius: theme.shape.borderRadius,
}));

const ButtonStyled = styled(Button)<ButtonProps & { component?: ElementType; htmlFor?: string }>(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        textAlign: 'center',
    },
}));

const ResetButtonStyled = styled(Button)<ButtonProps>(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        marginLeft: 0,
        textAlign: 'center',
        marginTop: theme.spacing(4),
    },
}));

const schema = yup.object().shape({
    name: yup.string().required(),
    industryId: yup.string().required(),
    hqAddress: yup.string(),
    hqProvince: yup.string(),
    hqPostalCode: yup.string(),
    hqCountryId: yup.string().required(),
    hqRegionId: yup.string().required(),
    revenueId: yup.string().required(),
    employeeSizeId: yup.string().required(),
    /*accountStatusId: yup.string().required()*/
});

const EditAccountDrawer = ({ open, onClose, account, onSaved }: Props) => {
    const { getDuplicateAccounts } = useDuplicateAccountList();
    const [imgSrc, setImgSrc] = useState<string>('');
    const [keyword, setKeyword] = useState({
        text: '',
        error: false,
        errorMessage: '',
    });

    const theme = useTheme();
    const {
        reset,
        control,
        setValue,
        handleSubmit,
        getValues,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: account,
        mode: 'onChange',
        resolver: yupResolver(schema),
    });
    const watchFields = watch(['keywords']);

    useEffect(() => {
        setImgSrc(`/api/accounts/images/${account.logoFileName}`);

        reset(account);
    }, [account]);

    const onSubmit = async (data: Account) => {
        try {
            const response = await client.post<ApiResponseType>(saveAccountEndpoint, data);

            if (response.data.succeeded) {
                ApplicationStore.update((s) => {
                    s.snackbarMessage = 'Record saved';
                    s.snackbarOpen = true;
                });

                onSaved(data);
                handleClose();
            } else {
                ApplicationStore.update((s) => {
                    s.snackbarMessage = 'Error saving record ' + response.data.errors[0].code;
                    s.snackbarOpen = true;
                });
            }
        } catch (err) {
            ApplicationStore.update((s) => {
                s.snackbarMessage = 'Error saving record ' + err.errors[0].code;
                s.snackbarOpen = true;
            });
        }
    };

    const handleClose = () => {
        setKeyword({
            text: '',
            error: false,
            errorMessage: '',
        });

        onClose();
    };

    const onAvatarChange = async (file: ChangeEvent, onChange: any) => {
        const reader = new FileReader();
        const { files } = file.target as HTMLInputElement;
        if (files && files.length !== 0) {
            const newId = uuid();

            const form = new FormData();
            form.append('files', files[0], files[0].name);
            form.append('imageId', newId);

            const response2 = await client.post(`/api/accounts/images`, form);

            const fileExtension = files[0].name.split('.').pop();
            setValue('logoFileName', `${newId}.${fileExtension}`);
            setImgSrc(`/api/accounts/images/${newId}.${fileExtension}`);
        }
    };

    const onResetImageSource = async () => {
        setImgSrc('');
    };

    const handleAddKeyword = () => {
        if (isEmpty(keyword.text)) {
            setKeyword({
                ...keyword,
                error: true,
                errorMessage: 'Please enter a valid keyword',
            });

            return;
        }

        const keywords = getValues('keywords');

        const existingKeywordIndex = keywords.findIndex((k) => k.keyword.toLowerCase() === keyword.text.toLowerCase());

        if (existingKeywordIndex === -1) {
            setValue('keywords', [...keywords, { id: uuid(), keyword: keyword.text, isDeleted: false }]);
        }

        setKeyword({
            text: '',
            error: false,
            errorMessage: '',
        });
    };

    const handleDeleteKeyword = (keyword: AccountKeyword) => {
        const keywords = getValues('keywords');

        const existingKeywordIndex = keywords.findIndex((k) => k.id === keyword.id);

        if (existingKeywordIndex !== -1) {
            const tempKeywords = [...keywords];

            tempKeywords[existingKeywordIndex].isDeleted = true;
            setValue('keywords', [...tempKeywords]);
        }
    };

    const keywords = getValues('keywords');

    return (
        <SimpleDialog open={open} onClose={handleClose} padding={0} fullHeight={false}>
            <SectionAppBar
                title={`Edit Account ${getValues('name') ?? ''}`}
                subTitle={''}
                buttons={
                    <>
                        <SaveButton
                            size="small"
                            type="submit"
                            variant="contained"
                            onClick={handleSubmit(onSubmit)}
                            icon={<SaveAltIcon />}
                        >
                            Save
                        </SaveButton>
                        <StandardButton
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={handleClose}
                            icon={<CloseIcon />}
                        >
                            Cancel
                        </StandardButton>
                    </>
                }
            />

            <FormWithLoader onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }} padding={2}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <ImgStyled
                                src={
                                    imgSrc === '' ||
                                    !imgSrc ||
                                    imgSrc === '/api/accounts/images/undefined' ||
                                    imgSrc === '/api/accounts/images/null'
                                        ? '/images/avatars/1.png'
                                        : imgSrc
                                }
                                alt="Profile Pic"
                            />
                            <RenderIf claimType={ClaimType.Accounts} claimValue={ClaimValue.UploadAccountLogo}>
                                <Box>
                                    <ButtonStyled
                                        size="small"
                                        component="label"
                                        variant="contained"
                                        htmlFor="account-settings-upload-image"
                                        sx={{ mb: 1 }}
                                        endIcon={<FileUploadIcon />}
                                    >
                                        Upload New Logo
                                        <Controller
                                            name="logoFileName"
                                            control={control}
                                            rules={{ required: true }}
                                            render={({ field: { value, onChange, onBlur } }) => (
                                                <input
                                                    hidden
                                                    type="file"
                                                    onChange={(e) => onAvatarChange(e, onChange)}
                                                    accept="image/png, image/jpeg"
                                                    id="account-settings-upload-image"
                                                />
                                            )}
                                        />
                                    </ButtonStyled>
                                    <ResetButtonStyled
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        onClick={onResetImageSource}
                                        sx={{ mb: 1, ml: 1 }}
                                        endIcon={<RestartAltIcon />}
                                    >
                                        Reset
                                    </ResetButtonStyled>
                                    <Typography variant="body2" sx={{ mt: 5 }}>
                                        Allowed PNG or JPEG. Max size of 800K.
                                    </Typography>
                                </Box>
                            </RenderIf>
                        </Grid>

                        <Grid item xs={12} md={4} lg={3}>
                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <Controller
                                    name="name"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            size="small"
                                            value={value}
                                            label="Name"
                                            onChange={(event) => {
                                                getDuplicateAccounts(event.target.value, account.id);
                                                onChange(event);
                                            }}
                                            placeholder="Account Name"
                                            error={Boolean(errors.name)}
                                        />
                                    )}
                                />
                                {/*{errors.name && <FormHelperText sx={{ color: 'error.main' }}>{errors.name.message}</FormHelperText>}*/}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={4} lg={3}>
                            <FormControl fullWidth sx={{ mb: 3 }} size="small">
                                <Controller
                                    name="hqAddress"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            size="small"
                                            value={value}
                                            label="HQ Address"
                                            onChange={onChange}
                                            placeholder="HQ Address"
                                            error={Boolean(errors.hqAddress)}
                                        />
                                    )}
                                />
                                {/*{errors.hqAddress && <FormHelperText sx={{ color: 'error.main' }}>{errors.hqAddress.message}</FormHelperText>}*/}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={4} lg={3}>
                            <FormControl fullWidth sx={{ mb: 3 }} size="small">
                                <Controller
                                    name="hqProvince"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            size="small"
                                            value={value}
                                            label="HQ Province"
                                            onChange={onChange}
                                            placeholder="HQ Province"
                                            error={Boolean(errors.hqProvince)}
                                        />
                                    )}
                                />
                                {/*{errors.hqProvince && <FormHelperText sx={{ color: 'error.main' }}>{errors.hqProvince.message}</FormHelperText>}*/}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={4} lg={3}>
                            <FormControl fullWidth sx={{ mb: 3 }} size="small">
                                <Controller
                                    name="hqPostalCode"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            size="small"
                                            value={value}
                                            label="HQ Postal Code"
                                            onChange={onChange}
                                            placeholder="HQ Postal Code"
                                            error={Boolean(errors.hqPostalCode)}
                                        />
                                    )}
                                />
                                {/*{errors.hqPostalCode && <FormHelperText sx={{ color: 'error.main' }}>{errors.hqPostalCode.message}</FormHelperText>}*/}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={4} lg={3}>
                            <FormControl fullWidth sx={{ mb: 3 }} size="small">
                                <InputLabel>Region</InputLabel>
                                <Controller
                                    name="hqRegionId"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            value={value}
                                            onChange={onChange}
                                            error={errors.hqRegionId?.message}
                                            label="Region"
                                            type="region"
                                        />
                                    )}
                                />
                                {/*{errors.hqRegionId && <FormHelperText sx={{ color: 'error.main' }}>{errors.hqRegionId.message}</FormHelperText>}*/}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={4} lg={3}>
                            <FormControl fullWidth sx={{ mb: 3 }} size="small">
                                <InputLabel>Country</InputLabel>
                                <Controller
                                    name="hqCountryId"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            value={value}
                                            onChange={onChange}
                                            error={errors.hqCountryId?.message}
                                            label="Country"
                                            type="country"
                                        />
                                    )}
                                />
                                {/*{errors.hqCountryId && <FormHelperText sx={{ color: 'error.main' }}>{errors.hqCountryId.message}</FormHelperText>}*/}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={4} lg={3}>
                            <FormControl fullWidth sx={{ mb: 3 }} size="small">
                                <InputLabel>Industry</InputLabel>
                                <Controller
                                    name="industryId"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            value={value}
                                            onChange={onChange}
                                            error={errors.industryId?.message}
                                            label="Industry"
                                            type="industry"
                                        />
                                    )}
                                />
                                {/*{errors.industryId && <FormHelperText sx={{ color: 'error.main' }}>{errors.industryId.message}</FormHelperText>}*/}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={4} lg={3}>
                            <FormControl fullWidth sx={{ mb: 3 }} size="small">
                                <InputLabel>Revenue</InputLabel>
                                <Controller
                                    name="revenueId"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            value={value}
                                            onChange={onChange}
                                            error={errors.revenueId?.message}
                                            label="Revenue"
                                            type="revenue"
                                        />
                                    )}
                                />
                                {/*{errors.revenueId && <FormHelperText sx={{ color: 'error.main' }}>{errors.revenueId.message}</FormHelperText>}*/}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={4} lg={3}>
                            <FormControl fullWidth sx={{ mb: 3 }} size="small">
                                <InputLabel>Employee Size</InputLabel>
                                <Controller
                                    name="employeeSizeId"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            value={value}
                                            onChange={onChange}
                                            error={errors.employeeSizeId?.message}
                                            label="Employee Size"
                                            type="employeesize"
                                        />
                                    )}
                                />
                                {/*{errors.employeeSizeId && <FormHelperText sx={{ color: 'error.main' }}>{errors.employeeSizeId.message}</FormHelperText>}*/}
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4} lg={3}>
                            <Box
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
                            >
                                <TextField
                                    sx={{ flex: 2 }}
                                    size="small"
                                    label="Add Keyword"
                                    value={keyword.text}
                                    onChange={(e) => setKeyword({ ...keyword, text: e.target.value })}
                                    error={keyword.error}
                                />
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleAddKeyword}
                                    endIcon={<AddIcon />}
                                >
                                    Add
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {keyword.error && (
                                    <Box sx={{ color: theme.palette.error.main }}>{keyword.errorMessage}</Box>
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4} lg={3}>
                            <Box sx={{ height: 150 }}>
                                {keywords &&
                                    keywords
                                        .filter((kw) => !kw.isDeleted)
                                        .map((kw) => (
                                            <Chip
                                                key={kw.id}
                                                size="small"
                                                label={kw.keyword}
                                                color="primary"
                                                sx={{ margin: 1 }}
                                                onDelete={() => handleDeleteKeyword(kw)}
                                            />
                                        ))}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </FormWithLoader>

            <DuplicateAccountList
                onAccountExists={(duplicate: DuplicateAccount) => {
                    reset(duplicate);
                }}
                onContinueNew={(accountName: string) => {
                    setValue('name', accountName);
                }}
            />
        </SimpleDialog>
    );
};

export default EditAccountDrawer;
